import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { useNavigate } from 'react-router-dom';

//Components
import { Button, Modal, ModalBody, ModalFooter } from "@gpstrackit/react-controls";
import { Alert, CircularProgress, Typography } from '@mui/material';
import { NotificationManager } from 'Components/Notifications';

//Helpers
import { copyToClipboard } from 'Core/data/Helpers';

//Actions
import { castImportManualImport, reset } from './redux/slice';

const MAX_ESNS = 300;

const parseEsns = (input) => {
    return [...new Set(
        input
            .split(/[\n,]+/)
            .map(s => s.trim())
            .filter(s => s.length > 0)
    )];
};

const CastImportManual = () => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { loading, openModal, result, error } = useSelector(state => state.castImportManualRedux);

    const [esnInput, setEsnInput] = useState('');

    const parsedEsns = parseEsns(esnInput);
    const isOverLimit = parsedEsns.length > MAX_ESNS;
    const isValid = parsedEsns.length > 0 && !isOverLimit;

    const handleClose = () => {
        dispatch(reset());
        setEsnInput('');
    };

    const handleImport = () => {
        if (!isValid) return;
        dispatch(castImportManualImport({ esns: parsedEsns }));
    };

    useEffect(() => {
        if (result?.transactionId) {
            const transactionId = result.transactionId;
            NotificationManager.success(
                messages['castImportManual_success'],
                messages['castImportManual_title'],
                10000,
                () => {
                    copyToClipboard(transactionId, messages['common_copied']);
                    navigate(`/app/transactions/list/${transactionId}`);
                },
                null, '', null
            );
            handleClose();
        }
    }, [result]);

    return (
        <Modal
            open={openModal}
            title={messages['castImportManual_title']}
            loading={loading}
            size="medium"
            handleClose={handleClose}
        >
            <ModalBody>
                <>
                    <Typography variant="body2" style={{ marginBottom: 8 }}>
                        {messages['castImportManual_description']}
                    </Typography>
                    <textarea
                        style={{
                            width: '100%',
                            height: 220,
                            padding: 8,
                            fontFamily: 'monospace',
                            fontSize: 13,
                            border: `1px solid ${isOverLimit ? '#F44336' : '#ccc'}`,
                            borderRadius: 4,
                            resize: 'vertical',
                            boxSizing: 'border-box',
                            outline: 'none'
                        }}
                        placeholder={messages['castImportManual_placeholder']}
                        value={esnInput}
                        onChange={(e) => setEsnInput(e.target.value)}
                        disabled={loading}
                    />
                    <Typography
                        variant="caption"
                        style={{ color: isOverLimit ? '#F44336' : '#666', display: 'block', marginTop: 4 }}
                    >
                        {parsedEsns.length} / {MAX_ESNS} {messages['castImportManual_esns_entered']}
                    </Typography>
                    {isOverLimit && (
                        <Alert severity="error" style={{ marginTop: 8 }}>
                            {messages['castImportManual_max_exceeded']}
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" style={{ marginTop: 8 }}>
                            {messages['castImportManual_error']}
                        </Alert>
                    )}
                </>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn-dark-blue"
                    onClick={handleImport}
                    disabled={loading || !isValid}
                >
                    {loading && (
                        <CircularProgress size={14} color="inherit" style={{ marginRight: 6 }} />
                    )}
                    {messages['castImportManual_import']}
                </Button>
                <Button onClick={handleClose}>
                    {messages['castImportManual_cancel']}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CastImportManual;
